import { type ChapterSectionDependency, findViolations } from '@shared/chapterSectionDependencies'
import { flattenChaptersToSectionKeys } from '@shared/chapterSectionsOrder'
import { useCallback, useMemo } from 'react'

import type { ChapterSectionEntry } from './useChapterSectionsBook'
import type { Book } from '../../../services/api'

export interface SectionDependencyView {
  /** Every dependency on the book, unfiltered. */
  deps: ChapterSectionDependency[]
  /** Deps whose `fromKey` is this section — it has to come before them. */
  outgoingByKey: Map<string, ChapterSectionDependency[]>
  /** Deps whose `toKey` is this section — it has to come after them. */
  incomingByKey: Map<string, ChapterSectionDependency[]>
  /** Ids of deps the current reader order breaks. */
  violations: Set<string>
  /** The sequence violations are measured against — the *active* reader order. */
  orderedKeys: string[]
  /** Human label for a section key, for chips and pickers. */
  labelByKey: (key: string) => string
}

/**
 * One source of truth for dependencies across every view that lists sections.
 *
 * Dependencies are stored as `filename:blockId` pairs, so the data never depends on
 * whether the book is arranged as chapters or as a flat sequence. The one thing that
 * does depend on structure is which linear order a violation is measured against —
 * resolved here, once, so no consumer has to know the active mode.
 */
export function useSectionDependencies(
  book: Book | null | undefined,
  entryByKey: Map<string, ChapterSectionEntry>,
): SectionDependencyView {
  const deps = useMemo(() => book?.settings?.chapterSectionDependencies ?? [], [book?.settings])

  const orderedKeys = useMemo(() => {
    if (book?.settings?.structure === 'flat') {
      return book?.settings?.flatSectionsOrder ?? []
    }
    return flattenChaptersToSectionKeys(book?.settings?.chapters ?? [])
  }, [book?.settings?.structure, book?.settings?.flatSectionsOrder, book?.settings?.chapters])

  const { outgoingByKey, incomingByKey } = useMemo(() => {
    const outgoing = new Map<string, ChapterSectionDependency[]>()
    const incoming = new Map<string, ChapterSectionDependency[]>()
    for (const dep of deps) {
      const out = outgoing.get(dep.fromKey) ?? []
      out.push(dep)
      outgoing.set(dep.fromKey, out)
      const inc = incoming.get(dep.toKey) ?? []
      inc.push(dep)
      incoming.set(dep.toKey, inc)
    }
    return { outgoingByKey: outgoing, incomingByKey: incoming }
  }, [deps])

  const violations = useMemo(() => new Set(findViolations(deps, orderedKeys).map((d) => d.id)), [deps, orderedKeys])

  const labelByKey = useCallback((key: string) => entryByKey.get(key)?.displayText ?? key, [entryByKey])

  return { deps, outgoingByKey, incomingByKey, violations, orderedKeys, labelByKey }
}
